import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { INVITE_STAGES } from '@/lib/labels';
import { INVITE_STAGE_FILTERS, type InviteStageFilter } from '@/lib/moneyPages';
import type { InviteStageCounts } from '@/hooks/useAdmin';

export type InviteStageTab = InviteStageFilter | 'all';

const TABS: readonly { value: InviteStageTab; label: string; hint: string; count: keyof InviteStageCounts }[] = [
  { value: 'all', label: 'All', hint: 'Every invite except hidden ones.', count: 'all' },
  ...INVITE_STAGE_FILTERS.map((s) => {
    const key = s === 'not_paid' ? ('notPaid' as const) : s;
    return { value: s, label: INVITE_STAGES[key].label, hint: INVITE_STAGES[key].hint, count: key };
  }),
];

/**
 * The Invites page's primary control: which stage of the flow to list, with
 * a live count on each tab. `value` is null when a link asked for a mix of
 * stages (e.g. Joined + Posted); no tab lights up and the filter chip says
 * what's applied.
 */
export function InviteStageTabs({
  value,
  counts,
  onChange,
  className,
}: {
  value: InviteStageTab | null;
  counts: InviteStageCounts | undefined;
  onChange: (tab: InviteStageTab) => void;
  className?: string;
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Stage"
      className={cn('-mb-px flex items-end gap-1 overflow-x-auto', className)}
    >
      {TABS.map((tab) => {
        const active = value === tab.value;
        const count = counts?.[tab.count];
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            title={tab.hint}
            onClick={() => onChange(tab.value)}
            className={cn(
              'relative inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap px-3 text-sm font-medium transition-colors duration-[120ms]',
              'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full',
              active
                ? 'text-foreground after:bg-foreground'
                : 'text-muted-foreground after:bg-transparent hover:text-foreground',
            )}
          >
            {tab.label}
            {count !== undefined && (
              <span
                className={cn(
                  'font-mono text-xs tabular-nums',
                  active ? 'text-foreground/70' : 'text-muted-foreground/80',
                )}
              >
                {count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
